import {
  ApolloClient,
  InMemoryCache,
  from,
  split,
  createHttpLink,
} from '@apollo/client'
import {onError} from '@apollo/client/link/error'
import {setContext} from '@apollo/client/link/context'
import {RetryLink} from '@apollo/client/link/retry'
import {getMainDefinition} from '@apollo/client/utilities'
import {graphqlConstants} from '../constants/graphqlConstants'
import {
  ERROR_MESSAGE,
  INTERNAL_SERVER_ERROR,
} from '../constants/commonConstants'
import {loginOnSessionTimeout, refreshToken} from '../utils/auth'
import {LocalStorageWrapper, persistCache} from 'apollo3-cache-persist'
import introspectionQueryResultData from '../fragmentTypes.json'
import {getCookie} from '../utils/genericUtils'
import {isSameOrBefore, fromUnixTimestamp} from '../utils/dateAdapter'

interface IDefinintion {
  kind: string
  operation?: string
}

export const createClient = async () => {
  const errorLink = onError(({graphQLErrors, networkError}) => {
    let isUnauthorized = false
    if (graphQLErrors) {
      graphQLErrors.forEach(error => {
        if (
          error.extensions &&
          ((error.extensions.response as {status?: number} | undefined)
            ?.status === 500 ||
            error.extensions.code === INTERNAL_SERVER_ERROR)
        ) {
          ;(error as any).message = ERROR_MESSAGE
        } else if (
          error.extensions &&
          ((error.extensions.response as {status?: number} | undefined)
            ?.status === 401 ||
            error.extensions.code === 'UNAUTHENTICATED')
        ) {
          isUnauthorized = true
        }
      })
    }
    if (networkError && networkError.message) {
      if (networkError.message.includes('401')) {
        isUnauthorized = true
      } else if (
        networkError.message.includes('400') ||
        networkError.message.includes('500')
      ) {
        ;(networkError as any).message = ERROR_MESSAGE
      }
    }
    if (isUnauthorized) {
      loginOnSessionTimeout()
    }
  })
  const httpLink = createHttpLink({
    uri: graphqlConstants.endpoint,
    credentials: 'include',
  })

  const link = split(({query}) => {
    const {kind}: IDefinintion = getMainDefinition(query)
    return kind === 'OperationDefinition'
  }, httpLink)

  const authLink = setContext((request, previousContext) => {
    const userInfo = getCookie('loginUserInfo')
    let isExpired = true
    let expiry = 0
    if (userInfo && decodeURIComponent(userInfo)) {
      const exp = JSON.parse(decodeURIComponent(userInfo)).exp
      expiry = Number(exp)
      isExpired = isSameOrBefore(fromUnixTimestamp(expiry), new Date())
    }
    if (isExpired) {
      refreshToken()
    }
    return {
      headers: {
        isCommonUser: request.variables && request.variables.isCommonUser,
        'Cache-Control': 'no-cache',
        useSixtbookToken: request.variables && request.variables.usesixtbook,
      },
    }
  })

  const retryLink = new RetryLink({
    attempts: {
      max: 10,
      retryIf: error => {
        // Retry on network errors or specific server-side errors (e.g., 503)
        return (
          error.networkError ||
          (error.graphQLErrors &&
            error.graphQLErrors.some(e => e.extensions.code === 'UNAVAILABLE'))
        )
      },
    },
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
  })

  // Apollo Client v3 uses possibleTypes instead of fragmentMatcher
  const cache = new InMemoryCache({
    possibleTypes: introspectionQueryResultData.__schema.types.reduce(
      (acc, type) => {
        if (type.possibleTypes) {
          acc[type.name] = type.possibleTypes.map(t => t.name)
        }
        return acc
      },
      {}
    ),
  })

  // This persist the cache in sessionStorage so that even if it is refreshed the cached values will be saved
  // await before instantiating ApolloClient, else queries might run before the cache is persisted
  await persistCache({
    cache,
    storage: new LocalStorageWrapper(window.sessionStorage),
  })

  const userInfo = getCookie('loginUserInfo')
  let name = ''
  let email = ''
  if (userInfo && decodeURIComponent(userInfo)) {
    const userInfoObj = JSON.parse(decodeURIComponent(userInfo))
    const {
      firstName = '',
      lastName = '',
      emailId = '',
      sxSFUsername = '',
      sxPayroll = '',
    } = userInfoObj
    name = firstName + ' ' + lastName
    email = emailId
    // Apollo Client v3: writeData is deprecated, use cache.modify or reactive variables
    // For now, we'll set these in the client's local resolvers
  }

  const client = new ApolloClient({
    cache,
    defaultOptions: {
      mutate: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      },
      watchQuery: {
        errorPolicy: 'ignore',
        fetchPolicy: 'network-only',
      },
    },
    link: from([retryLink, authLink, errorLink, link]),
    resolvers: {
      Query: {
        email: (rootValue, args, context, info) => {
          return email
        },
        name: (rootValue, args, context, info) => {
          return name
        },
      },
    },

    queryDeduplication: true,
  })

  return client
}
